import { useEffect, useState } from 'react';
import { Box, styled } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { DesktopTimePicker } from '@mui/x-date-pickers/DesktopTimePicker';
import type { Control, FieldValues } from 'react-hook-form';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';

import type { ReservationFormInput } from '../schema/reservationForm.schema';
import { useAppSelector } from '@/hooks/useAppSelector';
import * as reservationSlice from '@/data/reservation.slice';
import * as lookupSlice from '@/data/lookup.slice';
import {
  type TripArrayFieldKey,
  useReservationFormMachines,
} from '../ReservationFormMachinesContext';
import { useFieldActorSnapshot } from '../hooks/useFieldActorSnapshot';
import reservationUtils from '@/services/reservationUtils';

function mergeDatePart(
  base: DateTime | null,
  newDate: DateTime | null,
): DateTime | null {
  if (newDate === null) {
    return null;
  }
  if (!newDate.isValid) {
    return base;
  }
  if (!base?.isValid) {
    return null;
  }
  return base?.set({
    year: newDate.year,
    month: newDate.month,
    day: newDate.day,
  });
}

function mergeTimePart(
  base: DateTime | null,
  newTime: DateTime | null,
): DateTime | null {
  if (newTime === null) {
    return null;
  }
  if (!newTime.isValid) {
    return base;
  }
  if (!base?.isValid) {
    return newTime;
  }
  return base.set({
    hour: newTime.hour,
    minute: newTime.minute,
    second: newTime.second,
  });
}

const PREFIX = 'TripArrayFieldDateTimeInput';

const classes = {
  container: `${PREFIX}-container`,
  appointment: `${PREFIX}-appointment`,
  pickup: `${PREFIX}-pickup`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.container}`]: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    '& > *': { flex: '1 1 140px', minWidth: 0 },
    [`&.${classes.pickup}`]: {
      marginLeft: theme.spacing(4),
      marginTop: theme.spacing(-1),
      marginBottom: theme.spacing(1),
    },
    [theme.breakpoints.down('xs')]: {
      width: '100%',
    },
    [theme.breakpoints.up('md')]: {
      width: '75%',
    },
  },
}));

export function TripArrayFieldDateTimeInput({
  tripField,
  index,
  required,
  disabled,
  readOnly,
  minDateTime,
}: {
  /** Luxon datetime; `appointmentDate` / `pickupDate` are separate string fields synced from this control. */
  tripField: Extract<
    TripArrayFieldKey,
    'appointmentDateTime' | 'pickupDateTime'
  >;
  index: number;
  required?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  minDateTime?: DateTime | null;
}) {
  const { control, setValue, trigger, getValues } =
    useFormContext<ReservationFormInput>();
  const [timeInteracted, setTimeInteracted] = useState(false);
  const { t } = useTranslation('common');
  const { fieldRefs } = useReservationFormMachines();
  const actor = fieldRefs[tripField];
  useFieldActorSnapshot(actor);

  const timeZones = useAppSelector(lookupSlice.selectTimeZones);

  const pickupTimeZone = useWatch({
    control,
    name: `trips.${index}.pickupTimeZone`,
  }) as string | null | undefined;

  const pickupTimeZoneName = reservationUtils.getTimeZoneAbbrev(
    pickupTimeZone ?? '',
    timeZones,
  );

  const dateFieldLabel =
    tripField === 'appointmentDateTime'
      ? t('member.apptDate')
      : t('member.date');

  const timeFieldLabel =
    tripField === 'pickupDateTime' && pickupTimeZoneName
      ? `${t('member.time')} (${pickupTimeZoneName})`
      : t('member.time');

  const requiredFieldMessage = t('common.requiredField');
  const invalidDateMessage = t('common.invalidDate');
  const name = `trips.${index}.${tripField}` as const;
  const dateField =
    tripField === 'pickupDateTime' ? 'pickupDate' : 'appointmentDate';
  const dateFieldName = `trips.${index}.${dateField}` as const;
  const pairedDateIso = useWatch({
    control,
    name: dateFieldName,
  }) as string | undefined;
  const pairedDateValue = pairedDateIso
    ? DateTime.fromISO(pairedDateIso)
    : null;
  const estimatedTripTime = useAppSelector(
    reservationSlice.selectEstimatedTripTime,
  );
  const appointmentDateTime = useWatch({
    control,
    name: `trips.${index}.appointmentDateTime`,
  }) as DateTime | null | undefined;
  const pickupOffsetMinutes = estimatedTripTime?.totalPickupTime ?? 0;

  const selectedDate = (fieldValue: DateTime | null | undefined) =>
    fieldValue ?? pairedDateValue ?? null;

  const sendChange = (next: DateTime | null) => {
    actor.send({
      type: 'CHANGE',
      index,
      value: next?.toISO() ?? '',
    });
  };

  // Appointment drives calculate-pickup-time via the estimate machine's
  // subscription to this actor. Sync on blur only so typing/clock ticks do not
  // refetch on every keystroke. Pickup still syncs on change (no estimate fetch).
  const syncMachineOnChange = tripField === 'pickupDateTime';

  const syncPairedDateString = (next: DateTime | null) => {
    setValue(dateFieldName, next?.toISODate() ?? '', {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  const syncAppointmentMachineFromForm = () => {
    const current = getValues(name) as DateTime | null | undefined;
    sendChange(current ?? null);
  };

  /** Blur covers typing; onClose covers clock/calendar selection that may not blur. */
  const handlePickerBlur = () => {
    if (!syncMachineOnChange) {
      syncAppointmentMachineFromForm();
    }
    actor.send({ type: 'BLUR', index });
  };

  useEffect(() => {
    if (timeInteracted) {
      void trigger(name);
    }
  }, [timeInteracted, name, trigger]);

  return (
    <Controller
      name={name}
      control={control as unknown as Control<FieldValues>}
      rules={{
        validate: (value: DateTime | null) => {
          const hasPairedDate = pairedDateValue?.isValid ?? false;
          const hasValidDateTime = value?.isValid ?? false;

          if (value && !value.isValid) {
            return invalidDateMessage;
          }

          if (required) {
            if (!hasPairedDate && !hasValidDateTime) {
              return requiredFieldMessage;
            }
            if (hasPairedDate && !hasValidDateTime && timeInteracted) {
              return requiredFieldMessage;
            }
          }

          if (
            tripField === 'pickupDateTime' &&
            value?.isValid &&
            appointmentDateTime?.isValid
          ) {
            const minutesUntilAppointment = appointmentDateTime.diff(
              value,
              'minutes',
            ).minutes;
            if (minutesUntilAppointment < pickupOffsetMinutes) {
              return t('reservation.pickupTimeOffsetError', {
                minutes: pickupOffsetMinutes,
              });
            }
          }
          return true;
        },
      }}
      render={({ field, fieldState }) => {
        const effectiveSelectedDate = selectedDate(field.value);
        const pickupMaxDate =
          tripField === 'pickupDateTime' && appointmentDateTime?.isValid
            ? appointmentDateTime
            : undefined;
        const pickupMaxTime =
          tripField === 'pickupDateTime' &&
          appointmentDateTime?.isValid &&
          effectiveSelectedDate?.isValid &&
          effectiveSelectedDate.hasSame(appointmentDateTime, 'day')
            ? appointmentDateTime.minus({ minutes: pickupOffsetMinutes })
            : undefined;
        const effectiveMinTime =
          minDateTime?.isValid &&
          effectiveSelectedDate?.isValid &&
          effectiveSelectedDate.hasSame(minDateTime, 'day')
            ? minDateTime
            : undefined;

        const helperText = fieldState.error?.message;
        const hasDateOnlySelection =
          pairedDateValue?.isValid && !field.value?.isValid;
        const datePickerError = !!fieldState.error && !hasDateOnlySelection;
        const timePickerError =
          !!fieldState.error && (!hasDateOnlySelection || timeInteracted);
        const datePickerHelperText =
          tripField === 'appointmentDateTime' || !datePickerError
            ? null
            : helperText;
        const timePickerHelperText =
          tripField === 'appointmentDateTime' || !timePickerError
            ? null
            : helperText;
        const timeReferenceDate = effectiveSelectedDate?.isValid
          ? effectiveSelectedDate.startOf('day')
          : undefined;
        return (
          <StyledBox
            className={`${classes.container} ${tripField === 'appointmentDateTime' ? classes.appointment : classes.pickup}`}>
            <DatePicker
              label={dateFieldLabel}
              value={field.value ?? pairedDateValue ?? null}
              disabled={disabled}
              readOnly={readOnly ?? false}
              minDate={minDateTime ?? undefined}
              maxDate={pickupMaxDate}
              onClose={() => {
                if (!syncMachineOnChange) {
                  syncAppointmentMachineFromForm();
                }
              }}
              slotProps={{
                textField: {
                  variant: 'outlined',
                  required,
                  error: datePickerError,
                  helperText: datePickerHelperText,
                  onFocus: () => actor.send({ type: 'FOCUS', index }),
                  onBlur: () => {
                    handlePickerBlur();
                    field.onBlur();
                  },
                  sx: {
                    width: '100%',
                    '& .MuiInputAdornment-root .MuiIconButton-root': {
                      padding: '4px',
                      marginLeft: '-15px',
                    },
                    '& .MuiFormLabel-root[data-shrink="false"]': {
                      maxWidth: 'calc(100% - 48px)',
                    },
                  },
                },
              }}
              onChange={(date) => {
                setTimeInteracted(false);
                const next = mergeDatePart(field.value ?? null, date);
                const hasExistingDateTime =
                  field.value instanceof DateTime && field.value.isValid;

                if (hasExistingDateTime) {
                  field.onChange(next);
                  syncPairedDateString(next);
                  if (syncMachineOnChange) {
                    sendChange(next);
                  }
                  void trigger(name);
                  return;
                }

                // Keep date-only selection without fabricating a midnight time.
                field.onChange(null);
                setValue(dateFieldName, date?.toISODate() ?? '', {
                  shouldValidate: true,
                  shouldDirty: true,
                });
                if (syncMachineOnChange) {
                  sendChange(null);
                }
                void trigger(name);
              }}
            />
            <DesktopTimePicker
              label={timeFieldLabel}
              value={field.value ?? null}
              referenceDate={timeReferenceDate}
              disabled={disabled}
              readOnly={readOnly}
              minTime={effectiveMinTime}
              maxTime={pickupMaxTime}
              closeOnSelect={false}
              onClose={() => {
                if (!syncMachineOnChange) {
                  syncAppointmentMachineFromForm();
                }
              }}
              slotProps={{
                textField: {
                  variant: 'outlined',
                  required,
                  error: timePickerError,
                  helperText: timePickerHelperText,
                  onFocus: () => {
                    setTimeInteracted(true);
                    setValue(name, field.value ?? null, { shouldDirty: true });
                    actor.send({ type: 'FOCUS', index });
                  },
                  onBlur: () => {
                    handlePickerBlur();
                    field.onBlur();
                  },
                  sx: { width: '100%' },
                },
              }}
              onOpen={() => setTimeInteracted(true)}
              onChange={(time) => {
                if (time !== null && !time.isValid) {
                  return;
                }
                const fallbackBase =
                  pairedDateValue && pairedDateValue.isValid
                    ? pairedDateValue.startOf('day')
                    : null;
                const next = mergeTimePart(
                  (field.value ?? fallbackBase) as DateTime | null,
                  time,
                );
                field.onChange(next);
                syncPairedDateString(next ?? fallbackBase);
                if (syncMachineOnChange) {
                  sendChange(next);
                }
                void trigger(name);
              }}
            />
          </StyledBox>
        );
      }}
    />
  );
}
